// Lightweight API client. Adds the bearer token automatically, parses JSON,
// and surfaces structured errors. In dev, routes to the worker at :8787 via Vite proxy.

import { TOKEN_KEY } from "./constants";

const BASE = ""; // same-origin; Vite proxies /api → worker in dev

export interface ApiError extends Error {
  status: number;
  body?: unknown;
}

function makeError(status: number, body: unknown): ApiError {
  const e = new Error(
    typeof body === "object" && body && "error" in body
      ? String((body as { error: unknown }).error)
      : `HTTP ${status}`,
  ) as ApiError;
  e.status = status;
  e.body = body;
  return e;
}

let tokenProvider: () => string | null = () => null;
let on401: () => void = () => undefined;

export function configureClient(opts: { getToken: () => string | null; on401: () => void }) {
  tokenProvider = opts.getToken;
  on401 = opts.on401;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers || {});
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const token = tokenProvider();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(BASE + path, { ...init, headers });
  if (res.status === 401) {
    on401();
    throw makeError(401, await safeJson(res));
  }
  if (res.status === 403) {
    // Distinguish 403 reasons — turnstile_failed, rate_limited, suspended
    const body = await safeJson(res) as { error?: string; message?: string } | null;
    const errType = body?.error;
    if (errType === "turnstile_failed" || errType === "verification_failed") {
      throw makeError(403, { error: errType, message: body?.message || "Security check failed. Please refresh and try again." });
    }
    if (errType === "account_suspended" || errType === "account_inactive") {
      throw makeError(403, { error: errType, message: body?.message || "Account is not active." });
    }
    if (errType === "rate_limited") {
      throw makeError(403, { error: "rate_limited", message: body?.message || "Too many requests. Please wait a moment and try again." });
    }
    // Unknown 403 — WAF block, bot challenge, etc.
    throw makeError(403, { error: "blocked", message: body?.message || "Request was blocked. Please try again in a moment." });
  }
  if (res.status === 429) {
    const body = await safeJson(res);
    const msg = (body as { message?: string; reason?: string } | null)?.message
      || (body as { reason?: string } | null)?.reason
      || "You're sending too quickly. Please slow down.";
    throw makeError(429, { error: "rate_limited", message: msg });
  }
  if (res.status >= 500) {
    throw makeError(res.status, { error: "server_error", message: "Our servers hiccuped. Please try again in a moment." });
  }
  if (!res.ok) throw makeError(res.status, await safeJson(res));
  return (await res.json()) as T;
}

async function safeJson(res: Response): Promise<unknown> {
  try { return await res.json(); } catch { return null; }
}

export const api = {
  get: <T,>(p: string) => request<T>(p, { method: "GET" }),
  post: <T,>(p: string, body?: unknown) =>
    request<T>(p, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  patch: <T,>(p: string, body?: unknown) =>
    request<T>(p, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  put: <T,>(p: string, body?: unknown) =>
    request<T>(p, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  delete: <T,>(p: string) => request<T>(p, { method: "DELETE" }),
};

export type Folder = "inbox" | "sent" | "archive" | "trash" | "starred" | "drafts" | "spam";

export interface AttachmentSummary {
  id: number;
  filename: string;
  content_type: string;
  size_bytes: number;
}

export interface EmailAddress {
  address: string;
  name?: string;
}

export interface EmailSummary {
  id: number | string;
  threadId: string;
  from: EmailAddress;
  to: string[];
  subject: string;
  preview: string;
  hasAttachments: boolean;
  isRead: boolean;
  isStarred: boolean;
  direction: "inbound" | "outbound";
  receivedAt: string;
  size: number;
}

export interface EmailDetail extends EmailSummary {
  bodyText?: string | null;
  bodyHtml?: string | null;
}

export interface ThreadMessage {
  id: number | string;
  from_address: string;
  from_name: string;
  to_addresses: string;
  subject: string;
  body_preview: string;
  has_attachments: number;
  is_read: number;
  direction: "inbound" | "outbound";
  received_at: string;
}

export interface Thread {
  id: string;
  subject: string;
  last_message_at: string;
  message_count: number;
  is_archived: number;
}

export interface Alias {
  id: string;
  alias: string;
  is_active: number;
  created_at: string;
}

export interface Quota {
  tier: string;
  per_second_limit: number;
  per_minute_limit: number;
  per_hour_limit: number;
  per_day_limit: number;
  max_recipients: number;
  is_suspended: number;
  suspended_reason: string | null;
  email_verified: number;
  payment_on_file: number;
  bounce_count: number;
  complaint_count: number;
  total_sent: number;
  trust_score: number;
  storage_used_bytes: number;
}

export { TOKEN_KEY };
